/**
 * What: Derives recommended diagnostic survey questions for Shopify product claims, usage routines,
 *   ingredient proof, and other research inputs.
 * Transport / data-flow: Pure deterministic helper shared by Shopify contracts, Study Builder prefill,
 *   and survey authoring UI. No HTTP, React, D1, or LLM calls.
 *
 */
import * as z from 'zod';
/** Existing product-level scored elicitation, in scope for consumer-product-purchase-intent-v1. */
export const SHOPIFY_PRODUCT_CORE_QUESTION_TEXT = {
    understanding: 'Please describe in 1-2 sentences what you understand this product to be and what problem it solves.',
    purchase_intent: "Please explain in 1-2 sentences how likely you are to consider this product when it's available.",
    impression: 'Please explain in 1-2 sentences your overall impression of this product.',
};
/** Reviewed Pack Test defaults (#4720); source draft and methodology review are linked in the concept registry. */
export const SHOPIFY_PACK_TEST_QUESTION_TEXT = {
    understanding: 'With the accompanying product information available, please explain in 1-2 sentences how clearly or unclearly the packaging shown communicates what the product is and its main benefits. What about the packaging gives you that impression?',
    purchase_intent: "Please explain in 1-2 sentences how likely you are to purchase this product when it's available. What are the primary factors that influence your decision?",
    impression: 'Please explain in 1-2 sentences your overall impression of this pack design. What caught your attention most, positively or negatively?',
};
/** Reviewed Offer Test defaults (#4720); scores buying under shown terms, not incremental influence. */
export const SHOPIFY_OFFER_TEST_QUESTION_TEXT = {
    understanding: 'With the accompanying product information available, please explain in 1-2 sentences how clearly or unclearly this offer communicates the benefit and what you would need to do to receive it. What, if anything, is unclear?',
    purchase_intent: 'Considering this offer and the accompanying product information, please explain in 1-2 sentences how likely you are to purchase the product or bundle on the terms shown. What motivates you or holds you back?',
    impression: 'Please explain in 1-2 sentences your overall impression of this promotional offer. What stands out most, positively or negatively?',
};
export const SHOPIFY_CLAIM_ARCHETYPES = [
    'efficacy',
    'ingredient',
    'sustainability',
    'sensory_routine',
    'general_claim',
    'usage_clarity',
    'usage_friction',
    'ingredient_role',
    'ingredient_credibility',
    'packaging_image',
    'instructions_image',
    'advertising_image',
];
export const shopifyClaimArchetypeSchema = z.enum(SHOPIFY_CLAIM_ARCHETYPES);
export const shopifyRecommendedQuestionSchema = z.object({
    id: z.string(),
    text: z.string(),
    construct: z.string(),
    archetype: shopifyClaimArchetypeSchema,
    label: z.string(),
    rationale: z.string(),
});
const EFFICACY_PATTERN = /\b(?:clinical|clinically|trial|trials|proven|tested|reduce|reduces|reduction|increase|increases|boost|boosts|visible|results|fast|hours|days|weeks|percent|%|wrinkle|wrinkles|firming|anti[- ]aging|brighten|brightens|repair|whitening|strength|effective)\b/i;
const SUSTAINABILITY_PATTERN = /\b(?:sustainab|vegan|cruelty[- ]free|recycl|eco|plastic[- ]free|non[- ]gmo|fair[- ]trade|carbon|clean|ethical|biodegradable|refillable|compostable)\b/i;
const SENSORY_ROUTINE_PATTERN = /\b(?:texture|scent|fragrance|fragrance[- ]free|lightweight|absorb|finish|application|daily|routine|sensitive|gentle|non[- ]greasy|wear|feel|soothing|calming)\b/i;
const INGREDIENT_PATTERN = /\b(?:ingredient|ingredients|extract|extracts|organic|natural|cold[- ]pressed|peptide|peptides|acid|acids|botanic|botanical|oil|oils|pure|butter|vitamin|vitamins|herbal|ceramide|retinol|collagen)\b/i;
const ARCHETYPE_TEMPLATES = {
    efficacy: {
        construct: 'believability',
        label: 'Efficacy & Believability',
        text: 'What makes this claim feel believable or exaggerated?',
        rationale: 'Captures written shopper reactions on whether the stated performance or timeframe feels credible.',
    },
    ingredient: {
        construct: 'relevance',
        label: 'Ingredient Relevance',
        text: 'How important is this specific ingredient or formulation detail to your interest in this product?',
        rationale: 'Explains whether highlighted ingredients meaningfully drive shopper purchase interest.',
    },
    sustainability: {
        construct: 'trust',
        label: 'Credibility & Trust',
        text: 'How credible does this commitment feel for this product?',
        rationale: 'Identifies whether environmental and ethical claims build authentic trust or raise greenwashing doubts.',
    },
    sensory_routine: {
        construct: 'fit',
        label: 'Routine Fit',
        text: 'How does this product feature fit into your daily routine or preferences?',
        rationale: 'Explains how the product experience or routine integration shapes buying consideration.',
    },
    general_claim: {
        construct: 'clarity_and_proof',
        label: 'Clarity & Proof',
        text: 'What stands out about this claim, and what additional detail or proof would make it more convincing?',
        rationale: 'Highlights what makes the claim compelling and what proof points shoppers look for.',
    },
    usage_clarity: {
        construct: 'clarity',
        label: 'Usage & Care Clarity',
        text: 'How clearly do you understand the steps required to use, apply, or care for this product?',
        rationale: 'Captures whether usage instructions, preparation steps, or maintenance procedures are easy to understand.',
    },
    usage_friction: {
        construct: 'friction',
        label: 'Routine Fit & Convenience',
        text: 'What, if anything, feels inconvenient, demanding, or impractical about how this product is used or maintained?',
        rationale: 'Identifies protocol friction or perceived complexity that could deter daily usage or purchase.',
    },
    ingredient_role: {
        construct: 'relevance',
        label: 'Ingredient / Science Role',
        text: 'What role does this specific ingredient, formula, or material play in this product?',
        rationale: 'Checks whether shoppers understand the purpose and functional contribution of the highlighted ingredient or material.',
    },
    ingredient_credibility: {
        construct: 'credibility',
        label: 'Credibility & Proof',
        text: 'How credible and compelling does this formulation or ingredient detail make the product feel?',
        rationale: 'Captures whether ingredient science or materials provide a convincing reason-to-believe.',
    },
    packaging_image: {
        construct: 'clarity',
        label: 'Packaging clarity',
        text: "What, if anything, does the image communicate about the product's packaging? If anything is unclear or not shown, say so.",
        rationale: 'Explores what the packaging image communicates. These written answers do not change the test score.',
    },
    instructions_image: {
        construct: 'clarity',
        label: 'Instruction clarity',
        text: 'What, if anything, does the image tell you about how to use, prepare, or care for this product? If instructions are absent or unclear, say so.',
        rationale: 'Explores what shoppers can understand from the instruction image. These written answers do not change the test score.',
    },
    advertising_image: {
        construct: 'clarity',
        label: 'Advertising message',
        text: 'What product or offer message, if any, does the image communicate? If anything is unclear, say so.',
        rationale: 'Explores what the advertising image communicates in product context. These written answers do not change the test score.',
    },
};
/**
 * Classifies the claim archetype from raw text/key tokens.
 */
export function classifyClaimArchetype(content) {
    if (EFFICACY_PATTERN.test(content))
        return 'efficacy';
    if (SUSTAINABILITY_PATTERN.test(content))
        return 'sustainability';
    if (SENSORY_ROUTINE_PATTERN.test(content))
        return 'sensory_routine';
    if (INGREDIENT_PATTERN.test(content))
        return 'ingredient';
    return 'general_claim';
}
/**
 * Derives recommended diagnostic survey questions for a claim research input or treatment.
 * Returns questions in order of relevance; never violates Blind Elicitation.
 */
export function deriveRecommendedClaimQuestions(input) {
    const combinedText = [
        input.label ?? '',
        input.source?.key ?? '',
        input.source?.namespace ?? '',
        input.observedValue?.display ?? '',
    ].join(' ');
    const primaryArchetype = classifyClaimArchetype(combinedText);
    const primaryTemplate = ARCHETYPE_TEMPLATES[primaryArchetype];
    const questions = [
        {
            id: `rec-${primaryArchetype}`,
            archetype: primaryArchetype,
            construct: primaryTemplate.construct,
            label: primaryTemplate.label,
            text: primaryTemplate.text,
            rationale: primaryTemplate.rationale,
        },
    ];
    if (primaryArchetype !== 'general_claim') {
        const fallbackTemplate = ARCHETYPE_TEMPLATES.general_claim;
        questions.push({
            id: 'rec-general_claim',
            archetype: 'general_claim',
            construct: fallbackTemplate.construct,
            label: fallbackTemplate.label,
            text: fallbackTemplate.text,
            rationale: fallbackTemplate.rationale,
        });
    }
    return questions;
}
/**
 * Derives recommended diagnostic survey questions for a usage/routine research input or treatment.
 * Evaluates instructions clarity and daily routine fit/friction; strictly blind elicitation.
 */
export function deriveRecommendedUsageQuestions(_input) {
    const clarityTemplate = ARCHETYPE_TEMPLATES.usage_clarity;
    const frictionTemplate = ARCHETYPE_TEMPLATES.usage_friction;
    return [
        {
            id: 'rec-usage_clarity',
            archetype: 'usage_clarity',
            construct: clarityTemplate.construct,
            label: clarityTemplate.label,
            text: clarityTemplate.text,
            rationale: clarityTemplate.rationale,
        },
        {
            id: 'rec-usage_friction',
            archetype: 'usage_friction',
            construct: frictionTemplate.construct,
            label: frictionTemplate.label,
            text: frictionTemplate.text,
            rationale: frictionTemplate.rationale,
        },
    ];
}
/**
 * Derives recommended diagnostic survey questions for an ingredient or science proof research input or treatment.
 * Evaluates functional role and reason-to-believe credibility; strictly blind elicitation.
 */
export function deriveRecommendedIngredientQuestions(_input) {
    const roleTemplate = ARCHETYPE_TEMPLATES.ingredient_role;
    const credibilityTemplate = ARCHETYPE_TEMPLATES.ingredient_credibility;
    return [
        {
            id: 'rec-ingredient_role',
            archetype: 'ingredient_role',
            construct: roleTemplate.construct,
            label: roleTemplate.label,
            text: roleTemplate.text,
            rationale: roleTemplate.rationale,
        },
        {
            id: 'rec-ingredient_credibility',
            archetype: 'ingredient_credibility',
            construct: credibilityTemplate.construct,
            label: credibilityTemplate.label,
            text: credibilityTemplate.text,
            rationale: credibilityTemplate.rationale,
        },
    ];
}
/** Versioned diagnostic adaptations reviewed against QUESTION-LAYERS; no new score or anchor claim. */
export function imageConceptDiagnosticQuestions(concept) {
    const templates = {
        packaging: 'packaging_image',
        instructions: 'instructions_image',
        advertising: 'advertising_image',
    };
    const archetype = templates[concept];
    return [{ id: `rec-${archetype}-v1`, archetype, ...ARCHETYPE_TEMPLATES[archetype] }];
}
//# sourceMappingURL=product-research-questions.js.map